import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { consumePostLoginPath, routerTargetFromInternalPath } from '../utils/authRedirect';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password || isLoading) return;

    try {
      setIsLoading(true);
      await login(username, password, otpCode);
      navigate(routerTargetFromInternalPath(consumePostLoginPath(location.search)), { replace: true });
    } catch (err: any) {
      const code = String(err?.response?.data?.error || '').trim();
      if (code === 'mfa_required' || code === 'invalid_mfa_code') {
        setMfaRequired(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <LockIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
          <Typography component="h1" variant="h5">
            Cloud Configuration Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            Sign in to your account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {mfaRequired && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              Enter the 6-digit code from your authenticator app to complete sign-in.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              fullWidth
              name="otp_code"
              label="Authenticator code"
              id="otp_code"
              autoComplete="one-time-code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value)}
              disabled={isLoading}
              helperText={mfaRequired ? 'Required for accounts with MFA enabled.' : 'Optional unless your account requires MFA.'}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || !username || !password}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={22} sx={{ mr: 1 }} />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/forgot-password')}
              disabled={isLoading}
            >
              Forgot password?
            </Button>
          </Box>

          <Alert severity="info" sx={{ width: '100%', mt: 2 }}>
            This cloud is invite-only. Ask an admin to create or invite your account.
          </Alert>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
